import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Box,
  IconButton,
  Collapse,
  CircularProgress
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { fetchApi } from './fetchApi';

interface ArtikelZeile {
  artikel_id: number;
  sku: string;
  bezeichnung: string;
  gesamtmenge: number;
}

interface DetailZeile {
  lagerort_name: string;
  regalnummer: string | null;
  fachnummer: string | null;
  boxnummer: string;
  menge: number;
}

function ArtikelRow({ zeile }: { zeile: ArtikelZeile }) {
  const [offen, setOffen] = useState(false);
  const [detail, setDetail] = useState<DetailZeile[]>([]);
  const [laedt, setLaedt] = useState(false);

  async function toggleDetail() {
    if (!offen && detail.length === 0) {
      setLaedt(true);
      try {
        const res = await fetchApi(
          `/api/view/lagerstand-pro-artikel/${zeile.artikel_id}/detail`
        );
        const json = await res.json();
        setDetail(Array.isArray(json) ? json : []);
      } catch {
        setDetail([]);
      } finally {
        setLaedt(false);
      }
    }
    setOffen(prev => !prev);
  }

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}
        onClick={toggleDetail}
      >
        <TableCell sx={{ width: 40, pr: 0 }}>
          <IconButton size="small">
            {offen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{zeile.sku}</TableCell>
        <TableCell>{zeile.bezeichnung}</TableCell>
        <TableCell align="right"><strong>{zeile.gesamtmenge}</strong></TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={4} sx={{ py: 0, backgroundColor: '#fafafa' }}>
          <Collapse in={offen} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5, px: 2 }}>
              {laedt ? (
                <CircularProgress size={20} />
              ) : detail.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Keine Bestände gefunden.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Lagerort</TableCell>
                      <TableCell>Regal</TableCell>
                      <TableCell>Fach</TableCell>
                      <TableCell>Box</TableCell>
                      <TableCell align="right">Menge</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.map((d, i) => (
                      <TableRow key={i}>
                        <TableCell>{d.lagerort_name}</TableCell>
                        <TableCell>{d.regalnummer ?? '–'}</TableCell>
                        <TableCell>{d.fachnummer ?? '–'}</TableCell>
                        <TableCell>{d.boxnummer}</TableCell>
                        <TableCell align="right">{d.menge}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={4} sx={{ fontWeight: 600 }}>Summe</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {detail.reduce((s, d) => s + d.menge, 0)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function LagerstandProArtikelTabelle() {
  const [daten, setDaten] = useState<ArtikelZeile[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);

  useEffect(() => {
    fetchApi('/api/view/lagerstand-pro-artikel')
      .then(res => {
        if (!res.ok) throw new Error(String(res.status));
        return res.json();
      })
      .then(data => setDaten(Array.isArray(data) ? data : []))
      .catch((err: any) => {
        setFehler(
          err?.message === '401'
            ? 'Session abgelaufen. Bitte neu einloggen.'
            : 'Daten konnten nicht geladen werden.'
        );
      })
      .finally(() => setLaedt(false));
  }, []);

  return (
    <Container maxWidth="md">
      <Box mt={4} mb={2}>
        <Typography variant="h5">Lagerstand pro Artikel</Typography>
      </Box>

      {fehler && <Alert severity="error">{fehler}</Alert>}

      {laedt ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>SKU</TableCell>
                <TableCell>Bezeichnung</TableCell>
                <TableCell align="right">Gesamtmenge</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {daten.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Kein Bestand vorhanden.
                  </TableCell>
                </TableRow>
              ) : (
                daten.map(zeile => (
                  <ArtikelRow key={zeile.artikel_id} zeile={zeile} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}
